import { useRef, type KeyboardEvent } from 'react'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
  count?: number
}

// The segmented control: Auto · Light · Dark as radios, List · Recipes · Plan as
// tabs. Arrow keys move the selection, as both patterns expect.
export function Segmented<T extends string>({ options, value, onChange, ariaLabel, kind = 'radios', className = '' }: {
  options: SegmentedOption<T>[]
  value: T
  onChange: (v: T) => void
  ariaLabel: string
  kind?: 'radios' | 'tabs'
  className?: string
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const tabs = kind === 'tabs'

  function onKey(e: KeyboardEvent, i: number) {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const next = (i + step + options.length) % options.length
    onChange(options[next].value)
    refs.current[next]?.focus()
  }

  return (
    <div className={`ml-segmented ${className}`.trim()} role={tabs ? 'tablist' : 'radiogroup'} aria-label={ariaLabel}>
      {options.map((o, i) => {
        const on = o.value === value
        return (
          <button key={o.value} ref={el => { refs.current[i] = el }} type="button"
            role={tabs ? 'tab' : 'radio'}
            {...(tabs ? { 'aria-selected': on } : { 'aria-checked': on })}
            tabIndex={on ? 0 : -1}
            className={`ml-segmented__opt${on ? ' is-selected' : ''}`}
            onClick={() => onChange(o.value)} onKeyDown={e => onKey(e, i)}>
            {o.label}
            {o.count != null && <>{' '}<span className="ml-segmented__count ml-num">{o.count}</span></>}
          </button>
        )
      })}
    </div>
  )
}
